import Decimal from "decimal.js";
import { string } from "zod";
import { T } from "~/mydata/rpc/makexml";

export function has<T extends {}, P extends PropertyKey>(
  obj: T,
  key: P
): obj is T & { [K in P]: unknown } {
  return key in obj;
}

export function mapNumberToString() {
  return {
    from: (p?: number) => (p === undefined ? p : String(p)),
    into: (p?: string) => (p ? Number(p) : undefined),
  };
}
export function mapAmount() {
  return {
    from: (p?: number | string) => (p === undefined ? p : Number(p)),
    into: (p?: number) => p,
  };
}
export function mapUnknownToString() {
  return {
    from: (p?: unknown) => (p === undefined ? p : String(p)),
    into: (p?: string) => p,
  };
}
export const timestampToDate = (ts?: Date | string) =>
  ts === undefined ? ts : typeof ts === "string" ? new Date(ts) : ts;

export function mapTimestamp() {
  return {
    from: timestampToDate,
    into: (d?: Date) => d,
  };
}

export function DEBUG<T>(expression: T): T {
  console.debug("[DEBUG]: ", expression);
  return expression;
}

export const maybeString: (input: any) => string | undefined = (input) =>
  input === undefined ? undefined : String(input);
export const maybeNumber: <T>(input?: T) => number | undefined = (input) => {
  const n = Number(input);
  return Number.isNaN(n) ? undefined : n;
};

export const maybeType: <T>(
  typeguard: (input: unknown) => input is T
) => (input: unknown) => T | undefined = (guard) => (input: unknown) =>
  guard(input) ? input : undefined;

export const isDefined = <T>(
  input: T | undefined
): input is Exclude<T, undefined> => input !== undefined;

export const isSuccess = <T extends { success: boolean }>(
  input: T
): input is T & { success: true } => input.success === true;

export const maybeNativeEnum: <N extends number>(
  values: Record<PropertyKey, N>
) => (input: any) => N | undefined = (values) => (input: any) =>
  Object.values(values).includes(maybeNumber(input) as any)
    ? (maybeNumber(input) as any)
    : undefined;

export const maybeEnum: <S extends string>(
  values: readonly S[]
) => (input: any) => S | undefined = (values) => (input: any) =>
  input === undefined
    ? undefined
    : values.includes(String(input) as any)
    ? (String(input) as any)
    : undefined;

export const debugJSX = <T>(value: T) => {
  console.log({ value });
  return value;
};

export const unreachable = (_: never) => {
  throw new Error("unreachable");
};

export const appendItem = <T>(items: T[], item: T) => [...items, item];
export const deleteItem = <T>(items: T[], item: T, index: number) => [
  ...items.slice(0, index),
  ...items.slice(index + 1),
];
export const putItem = <T>(items: T[], item: T, index: number) => [
  ...items.slice(0, index),
  item,
  ...items.slice(index + 1),
];

export const replaceDotWithComma = (decimalNumber: string | number | undefined) => {
  const number = decimalNumber
    ? typeof decimalNumber === "string"
      ? decimalNumber
      : new Decimal(decimalNumber).toFixed(2)
    : "";

  return number.replace(".", ",");
};
